
import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase/client';
import { createNotification } from '../../../lib/notifications/createNotification';
import { erstelleTicket } from '../../../lib/tickets/erstelleTicket';
import { X, Loader2, Search, Building2, Users } from 'lucide-react';
import ListTextarea from '../../../components/ui/ListTextarea';
import KiTextHelfer from '../../../components/ki/KiTextHelfer';
import { ladeChecklistenVorlagen, fuegeChecklisteAn, type ChecklistenVorlage } from '../../../lib/tickets/checklistenVorlagen';
import {
  Buildings, CalendarCheck, Circle, LinkSimple, ListChecks, Minus, Tag, TagSimple, TextAlignLeft, Ticket, User, Users as PhUsers,
} from '@phosphor-icons/react';
import { FormRahmen } from '../../../components/island/form/FormRahmen';
import { FormAbschnitt } from '../../../components/island/form/FormAbschnitt';
import { FeldHuelle, FormFeld, FormZeile } from '../../../components/island/form/FormFeld';
import { FormAuswahl } from '../../../components/island/form/FormAuswahl';
import { FormSuche } from '../../../components/island/form/FormSuche';
import { FormKnopfleiste } from '../../../components/island/form/FormKnopfleiste';
import { PRIO_FARBE, ZEILEN_ICON } from '../../../components/island/form/form-stil';

interface Kategorie {
  id: string;
  name: string;
  parent_id: string | null;
  farbe: string;
}

interface TicketInitialData {
  titel?: string;
  beschreibung?: string;
  objekt_id?: string;
  mieter_id?: string;
  kategorie_id?: string;
  prioritaet?: string;
  faellig_am?: string | null;
  einheit_id?: string | null;
  quelle_email_id?: string | null;
  absender_typ?: string | null;
  unterkategorie_id?: string | null;
  erkannte_aufgabe?: string | null;
  naechster_schritt?: string | null;
  bearbeiter_vorschlag_id?: string | null;
  bearbeiter_vorschlag_typ?: 'profile' | 'team' | null;
  dienstleister_vorschlag_id?: string | null;
  ki_confidence?: number | null;
  ist_rechtlich_sensibel?: boolean;
}

interface TicketFormularProps {
  kategorien: Kategorie[];
  teamMembers: { id: string; full_name: string }[];
  teams?: { id: string; name: string }[];
  userProfile?: any;
  onClose: () => void;
  onCreated: (ticketId?: string) => void;
  initialData?: TicketInitialData;
  /**
   * 'pille' rendert ohne Overlay/eigenen Header für den Island-Formularbereich
   * (dunkles Theme — die Pille liefert Titelzeile + Schließen), wie EmailVerfassen.
   */
  /** 'immo' = neues Design (Immobilienbereich): Design-Tokens, inline statt Pille/Modal. */
  variant?: 'modal' | 'pille' | 'immo';
}

// Klassen-Paletten je Variante (hell = zentriertes Modal, dunkel = Pille)
const LABEL_MODAL = 'text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block';
const LABEL_PILLE = 'text-[9px] font-black text-white/40 uppercase tracking-widest mb-1.5 block';
const INPUT_MODAL =
  'w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold text-gray-900 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#1a1d24]/20 focus:border-[#1a1d24]/40';
const INPUT_PILLE =
  'w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm font-bold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40 focus:border-[#c4a77d]/40';
// Neues Design (README §4): Label 12/700 --ink3, Feld 50 px, Radius 15, Fläche --bg mit Linie
const LABEL_IMMO = 'text-[12px] font-bold text-[var(--ink3)] mb-1.5 block';
const INPUT_IMMO =
  'w-full min-h-[50px] px-[14px] py-3 rounded-[15px] bg-[var(--bg)] text-[15px] font-bold text-[var(--ink)] placeholder:text-[var(--ink3)] outline-none shadow-[inset_0_0_0_1px_var(--line)] focus:shadow-[inset_0_0_0_2px_var(--acc)] transition-all';

const TicketFormular: React.FC<TicketFormularProps> = ({
  kategorien, teamMembers, teams = [], userProfile, onClose, onCreated, initialData, variant = 'modal'
}) => {
  const pille = variant === 'pille';
  const immo = variant === 'immo';
  /** Klassen je Variante: Pille (dunkel), Modal (hell), immo (neues Design mit Tokens). */
  const w = (pilleCls: string, modalCls: string, immoCls: string) => (immo ? immoCls : pille ? pilleCls : modalCls);
  const labelCls = w(LABEL_PILLE, LABEL_MODAL, LABEL_IMMO);
  const inputCls = w(INPUT_PILLE, INPUT_MODAL, INPUT_IMMO);
  // Native Dropdown-Panels sind hell — Optionen immer dunkel beschriften.
  // immo: Farbe erbt vom Farbschema (hell/dunkel), sonst wären Optionen im dunklen Menü unlesbar
  const optionCls = immo ? '' : 'text-gray-900';
  const dateCls = w(`${INPUT_PILLE} [color-scheme:dark]`, INPUT_MODAL, INPUT_IMMO);
  const chipCls = w(
    'flex items-center gap-3 px-4 py-3 bg-white/5 border border-[#c4a77d]/40 rounded-xl',
    'flex items-center gap-3 px-4 py-3 bg-gray-50 border border-[#1a1d24]/20 rounded-xl',
    'flex items-center gap-3 px-4 py-3 rounded-[15px] bg-[var(--acc-soft)]');
  const chipTextCls = w('text-sm font-bold text-white flex-1', 'text-sm font-bold text-gray-900 flex-1', 'text-sm font-bold text-[var(--ink)] flex-1');
  const searchIconCls = w('absolute left-3 top-1/2 -translate-y-1/2 text-white/30', 'absolute left-3 top-1/2 -translate-y-1/2 text-gray-300', 'absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink3)]');
  const searchInputCls = w(
    'w-full pl-9 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm font-bold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40',
    'w-full pl-9 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold text-gray-900 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#1a1d24]/20',
    `${INPUT_IMMO} pl-9`);
  const entfernenCls = w('text-white/30 hover:text-red-400', 'text-gray-300 hover:text-red-400', 'text-[var(--ink3)] hover:text-[var(--bad)]');
  const dropdownPanelCls = immo
    ? 'absolute top-full left-0 right-0 mt-1 rounded-[15px] z-10 overflow-hidden bg-[var(--bg)] shadow-[0_12px_30px_-8px_var(--shade),inset_0_0_0_1px_var(--line)]'
    : 'absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-100 z-10 overflow-hidden';
  const dropdownItemCls = immo
    ? 'w-full text-left px-4 py-2.5 hover:bg-[var(--hover)] transition-colors border-b border-[var(--line)] last:border-0'
    : 'w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0';
  const trefferTitelCls = immo ? 'text-xs font-bold text-[var(--ink)]' : 'text-xs font-bold text-gray-900';
  const trefferSubCls = immo ? 'text-[11px] text-[var(--ink3)]' : 'text-[9px] text-gray-400';
  const [titel, setTitel] = useState(initialData?.titel || '');
  const [beschreibung, setBeschreibung] = useState(initialData?.beschreibung || '');
  const [checklistenVorlagen, setChecklistenVorlagen] = useState<ChecklistenVorlage[]>([]);

  // Checklisten-Vorlagen (best effort — ohne Vorlagen bleibt das Dropdown einfach weg).
  useEffect(() => {
    let cancelled = false;
    ladeChecklistenVorlagen()
      .then((v) => { if (!cancelled) setChecklistenVorlagen(v); })
      .catch((e) => console.warn('Checklisten-Vorlagen nicht ladbar', e));
    return () => { cancelled = true; };
  }, []);
  const [prioritaet, setPrioritaet] = useState('mittel');
  const [kategorieId, setKategorieId] = useState('');
  const [subKategorieId, setSubKategorieId] = useState('');
  const [zustaendigId, setZustaendigId] = useState('');
  const [faelligAm, setFaelligAm] = useState(initialData?.faellig_am || '');
  const [wiedervorlageAm, setWiedervorlageAm] = useState('');
  const [saving, setSaving] = useState(false);

  // Objekt search
  const [objektSearch, setObjektSearch] = useState('');
  const [objektResults, setObjektResults] = useState<any[]>([]);
  const [selectedObjekt, setSelectedObjekt] = useState<any>(null);
  const [showObjektSearch, setShowObjektSearch] = useState(false);

  // Mieter search
  const [mieterSearch, setMieterSearch] = useState('');
  const [mieterResults, setMieterResults] = useState<any[]>([]);
  const [selectedMieter, setSelectedMieter] = useState<any>(null);
  const [showMieterSearch, setShowMieterSearch] = useState(false);

  const hauptKategorien = kategorien.filter(k => k.parent_id === null);
  const subKategorien = kategorien.filter(k => k.parent_id === kategorieId);

  // Vorausfüllung aus initialData (z.B. aus E-Mail)
  useEffect(() => {
    if (initialData?.prioritaet) setPrioritaet(initialData.prioritaet);
    const leafId = initialData?.unterkategorie_id || initialData?.kategorie_id;
    if (leafId) {
      const k = kategorien.find((x) => x.id === leafId);
      if (k) {
        if (k.parent_id) { setKategorieId(k.parent_id); setSubKategorieId(k.id); }
        else { setKategorieId(k.id); }
      }
    }
    if (initialData?.objekt_id) {
      supabase.from('objekte').select('id, bezeichnung, strasse, hausnummer, plz, stadt').eq('id', initialData.objekt_id).single()
        .then(({ data }) => { if (data) setSelectedObjekt(data); });
    }
    if (initialData?.mieter_id) {
      supabase.from('mieter').select('id, vorname, nachname, email').eq('id', initialData.mieter_id).single()
        .then(({ data }) => { if (data) setSelectedMieter(data); });
    }
  }, []);

  // Objekt suche
  useEffect(() => {
    if (objektSearch.length < 2) { setObjektResults([]); return; }
    const timer = setTimeout(async () => {
      const { data } = await supabase.from('objekte')
        .select('id, bezeichnung, strasse, hausnummer, plz, stadt')
        .or(`bezeichnung.ilike.%${objektSearch}%,strasse.ilike.%${objektSearch}%`)
        .limit(5);
      setObjektResults(data || []);
    }, 250);
    return () => clearTimeout(timer);
  }, [objektSearch]);

  // Mieter suche
  useEffect(() => {
    if (mieterSearch.length < 2) { setMieterResults([]); return; }
    const timer = setTimeout(async () => {
      const { data } = await supabase.from('mieter')
        .select('id, vorname, nachname, email')
        .or(`vorname.ilike.%${mieterSearch}%,nachname.ilike.%${mieterSearch}%`)
        .limit(5);
      setMieterResults(data || []);
    }, 250);
    return () => clearTimeout(timer);
  }, [mieterSearch]);

  const handleSubmit = async () => {
    if (!titel.trim()) return;
    setSaving(true);

    try {
      const finalKategorieId = subKategorieId || kategorieId || null;

      const { id: ticketId, ticket_nummer } = await erstelleTicket(
        {
          titel: titel.trim(),
          beschreibung: beschreibung.trim(),
          objekt_id: selectedObjekt?.id,
          mieter_id: selectedMieter?.id,
          kategorie_id: finalKategorieId || undefined,
          prioritaet,
          einheit_id: initialData?.einheit_id,
          quelle_email_id: initialData?.quelle_email_id,
          absender_typ: initialData?.absender_typ,
          unterkategorie_id: subKategorieId || null,
          erkannte_aufgabe: initialData?.erkannte_aufgabe,
          naechster_schritt: initialData?.naechster_schritt,
          bearbeiter_vorschlag_id: initialData?.bearbeiter_vorschlag_id,
          bearbeiter_vorschlag_typ: initialData?.bearbeiter_vorschlag_typ,
          dienstleister_vorschlag_id: initialData?.dienstleister_vorschlag_id,
          ki_confidence: initialData?.ki_confidence,
          ist_rechtlich_sensibel: initialData?.ist_rechtlich_sensibel,
        },
        userProfile?.id || null,
      );

      // Zuständigkeit kann Mitarbeiter ODER Team sein (Typ aus der jeweiligen Liste ableiten).
      const zustaendigTyp: 'mitarbeiter' | 'team' | null = zustaendigId
        ? (teams.some(t => t.id === zustaendigId) ? 'team' : 'mitarbeiter') : null;

      // Formular-spezifische Zusatzfelder nachsetzen (nicht Teil der Vorlage)
      if (zustaendigId || faelligAm || wiedervorlageAm) {
        await supabase.from('tickets').update({
          zustaendig_id: zustaendigId || null,
          zustaendig_typ: zustaendigTyp,
          faellig_am: faelligAm || null,
          wiedervorlage_am: wiedervorlageAm || null,
        }).eq('id', ticketId);
        if (zustaendigId) await supabase.from('entity_links').insert({
          source_type: 'ticket', source_id: ticketId,
          target_type: zustaendigTyp === 'team' ? 'team' : 'profile', target_id: zustaendigId, link_type: 'zustaendigkeit',
        });
      }

      // Benachrichtigung nur für einzelne Mitarbeiter (ein Team hat keinen einzelnen Empfänger)
      if (zustaendigId && zustaendigTyp !== 'team' && zustaendigId !== userProfile?.id) {
        await createNotification({
          benutzerId: zustaendigId,
          modul: 'tickets',
          typ: 'neue_zuweisung',
          nachricht: `Ticket ${ticket_nummer} wurde Ihnen zugewiesen: ${titel}`,
          referenzId: ticketId,
          referenzTyp: 'ticket',
          linkPfad: '/admin/tickets',
          erstelltVon: userProfile?.id,
        });
      }

      onCreated(ticketId);
    } catch (err) {
      console.error('Error creating ticket:', err);
    } finally {
      setSaving(false);
    }
  };

  const inhalt = (
    <div className={w('p-4 space-y-5', 'p-6 space-y-5', 'space-y-5')}>
      {/* Titel */}
      <div>
        <label className={labelCls}>Titel *</label>
        <input
          type="text"
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          placeholder="z.B. Heizung defekt 3. OG"
          className={inputCls}
        />
      </div>

      {/* Beschreibung (+ Checklisten-Vorlage: kopiert den Ablauf ans Ende, editierbar) */}
      <div>
        {/* Auf dem Handy stapeln: Label über den Bedienelementen. Die Selects
            rendern unter 768px mit 16px Schrift (iOS-Zoom-Regel) und passen sonst
            nicht mehr neben das Label — die Zeile lief seitlich aus dem Bild.
            Deshalb erst ab "platz" (genug Breite UND Höhe) nebeneinander. */}
        <div className="flex flex-col gap-1.5 platz:flex-row platz:items-center platz:justify-between mb-1.5">
          <label className={`${labelCls} mb-0`}>Beschreibung</label>
          <div className="flex items-center gap-1.5 w-full min-w-0 platz:w-auto">
            {checklistenVorlagen.length > 0 && (
              <select
                value=""
                onChange={(e) => {
                  const v = checklistenVorlagen.find((cv) => cv.id === e.target.value);
                  if (v) setBeschreibung((prev) => fuegeChecklisteAn(prev, v.checkliste));
                }}
                className={`min-w-0 flex-1 platz:flex-none ${w(
                  'text-[10px] font-bold text-white/60 bg-white/5 border border-white/10 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40',
                  'text-[10px] font-bold text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#1a1d24]/20',
                  'text-[12px] font-bold text-[var(--ink)] bg-[var(--fill2)] rounded-[12px] px-2 h-8 outline-none')}`}
              >
                <option value="" className={optionCls}>☑ Checkliste einfügen…</option>
                {checklistenVorlagen.map((v) => (
                  <option key={v.id} value={v.id} className={optionCls}>{v.name}</option>
                ))}
              </select>
            )}
            <KiTextHelfer
              aktuellerText={beschreibung}
              kontext={{ titel }}
              onUebernehmen={setBeschreibung}
              richtung="unten"
            />
          </div>
        </div>
        <ListTextarea
          value={beschreibung}
          onChange={(e) => setBeschreibung(e.target.value)}
          rows={3}
          placeholder="Details zum Ticket..."
          className={`${inputCls} resize-none`}
        />
      </div>

      {/* Priorität + Kategorie */}
      {/* Handy: eine Spalte (Breakpoint-Vertrag) — zwei 16px-Selects nebeneinander
          passen auf 390px nicht mehr. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Priorität</label>
          <select value={prioritaet} onChange={(e) => setPrioritaet(e.target.value)} className={inputCls}>
            <option value="niedrig" className={optionCls}>⚪ Niedrig</option>
            <option value="mittel" className={optionCls}>🟡 Mittel</option>
            <option value="hoch" className={optionCls}>🟠 Hoch</option>
            <option value="kritisch" className={optionCls}>🔴 Kritisch</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Kategorie</label>
          <select
            value={kategorieId}
            onChange={(e) => { setKategorieId(e.target.value); setSubKategorieId(''); }}
            className={inputCls}
          >
            <option value="" className={optionCls}>— Wählen —</option>
            {hauptKategorien.map(k => <option key={k.id} value={k.id} className={optionCls}>{k.name}</option>)}
          </select>
        </div>
      </div>

      {/* Unterkategorie */}
      {subKategorien.length > 0 && (
        <div>
          <label className={labelCls}>Unterkategorie</label>
          <select value={subKategorieId} onChange={(e) => setSubKategorieId(e.target.value)} className={inputCls}>
            <option value="" className={optionCls}>— Optional —</option>
            {subKategorien.map(k => <option key={k.id} value={k.id} className={optionCls}>{k.name}</option>)}
          </select>
        </div>
      )}

      {/* Objekt Suche */}
      <div>
        <label className={labelCls}>Objekt verknüpfen</label>
        {selectedObjekt ? (
          <div className={chipCls}>
            <Building2 size={16} className={w('text-[#c4a77d] shrink-0', 'text-[#1a1d24] shrink-0', 'text-[var(--acc)] shrink-0')} />
            <span className={chipTextCls}>{selectedObjekt.bezeichnung}</span>
            <button onClick={() => setSelectedObjekt(null)} className={entfernenCls}><X size={14} /></button>
          </div>
        ) : (
          <div className="relative">
            <Search size={14} className={searchIconCls} />
            <input
              type="text"
              value={objektSearch}
              onChange={(e) => { setObjektSearch(e.target.value); setShowObjektSearch(true); }}
              onFocus={() => setShowObjektSearch(true)}
              placeholder="Objekt suchen..."
              className={searchInputCls}
            />
            {showObjektSearch && objektResults.length > 0 && (
              <div className={dropdownPanelCls}>
                {objektResults.map(o => (
                  <button
                    key={o.id}
                    onClick={() => { setSelectedObjekt(o); setObjektSearch(''); setShowObjektSearch(false); }}
                    className={dropdownItemCls}
                  >
                    <p className={trefferTitelCls}>{o.bezeichnung}</p>
                    <p className={trefferSubCls}>{o.strasse} {o.hausnummer}, {o.plz} {o.stadt}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mieter Suche */}
      <div>
        <label className={labelCls}>Mieter (optional)</label>
        {selectedMieter ? (
          <div className={pille || immo ? chipCls : 'flex items-center gap-3 px-4 py-3 bg-blue-50/50 border border-blue-100 rounded-xl'}>
            <Users size={16} className="text-blue-400 shrink-0" />
            <span className={chipTextCls}>{selectedMieter.vorname} {selectedMieter.nachname}</span>
            <button onClick={() => setSelectedMieter(null)} className={entfernenCls}><X size={14} /></button>
          </div>
        ) : (
          <div className="relative">
            <Search size={14} className={searchIconCls} />
            <input
              type="text"
              value={mieterSearch}
              onChange={(e) => { setMieterSearch(e.target.value); setShowMieterSearch(true); }}
              onFocus={() => setShowMieterSearch(true)}
              placeholder="Mieter suchen..."
              className={searchInputCls}
            />
            {showMieterSearch && mieterResults.length > 0 && (
              <div className={dropdownPanelCls}>
                {mieterResults.map(m => (
                  <button
                    key={m.id}
                    onClick={() => { setSelectedMieter(m); setMieterSearch(''); setShowMieterSearch(false); }}
                    className={dropdownItemCls}
                  >
                    <p className={trefferTitelCls}>{m.vorname} {m.nachname}</p>
                    <p className={trefferSubCls}>{m.email}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Zuständig + Fällig */}
      {/* Handy: eine Spalte (Breakpoint-Vertrag) — zwei 16px-Selects nebeneinander
          passen auf 390px nicht mehr. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Zuständig</label>
          <select value={zustaendigId} onChange={(e) => setZustaendigId(e.target.value)} className={inputCls}>
            <option value="" className={optionCls}>— Niemand —</option>
            <optgroup label="Mitarbeiter" className={optionCls}>
              {teamMembers.map(m => <option key={m.id} value={m.id} className={optionCls}>{m.full_name || 'Unbekannt'}</option>)}
            </optgroup>
            {teams.length > 0 && (
              <optgroup label="Teams" className={optionCls}>
                {teams.map(t => <option key={t.id} value={t.id} className={optionCls}>{t.name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
        <div>
          <label className={labelCls}>Fällig am</label>
          <input type="date" value={faelligAm} onChange={(e) => setFaelligAm(e.target.value)} className={dateCls} />
        </div>
      </div>

      {/* Wiedervorlage */}
      <div>
        <label className={labelCls}>Wiedervorlage am</label>
        <input type="date" value={wiedervorlageAm} onChange={(e) => setWiedervorlageAm(e.target.value)} className={dateCls} />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={onClose}
          className={w(
            'flex-1 py-3.5 border border-white/10 text-white/60 rounded-xl text-[10px] font-black uppercase tracking-widest hover:text-white transition-all',
            'flex-1 py-3.5 bg-gray-50 text-gray-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-100 transition-all',
            'flex-1 h-12 rounded-[15px] bg-[var(--fill2)] text-[14px] font-bold text-[var(--ink)] hover:bg-[var(--hover)] transition-all')}
        >
          Abbrechen
        </button>
        <button
          onClick={handleSubmit}
          disabled={!titel.trim() || saving}
          className={w(
            'flex-1 py-3.5 bg-[#c4a77d] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#b3966c] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2',
            'flex-1 py-3.5 bg-[#1a1d24] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2',
            'flex-1 h-12 rounded-[15px] [background:var(--acc-fill)] text-[var(--acc-ink)] text-[14px] font-extrabold shadow-[var(--acc-sh)] transition-all disabled:opacity-45 disabled:cursor-not-allowed flex items-center justify-center gap-2')}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          Ticket erstellen
        </button>
      </div>
    </div>
  );

  // Pille (Handoff Pillen-Formulare): Bausteine, gleiche Zustände und Handler wie oben.
  if (pille) {
    const leer = { iconFlaeche: ZEILEN_ICON.leer.flaeche, iconFarbe: ZEILEN_ICON.leer.farbe, icon: Minus };
    const neutral = { iconFlaeche: ZEILEN_ICON.gedaempft.flaeche, iconFarbe: ZEILEN_ICON.gedaempft.farbe };
    const PRIO = [
      { wert: 'niedrig', label: 'Niedrig' }, { wert: 'mittel', label: 'Mittel' },
      { wert: 'hoch', label: 'Hoch' }, { wert: 'kritisch', label: 'Kritisch' },
    ] as const;
    return (
      <FormRahmen knopfleiste={
        <FormKnopfleiste abbrechen={{ label: 'Abbrechen', onClick: onClose }}
          haupt={{
            label: 'Ticket erstellen', icon: Ticket, typ: 'button', onClick: handleSubmit, laedt: saving,
            gesperrt: !titel.trim() && !saving, gesperrtGrund: 'Erst einen Titel eingeben',
          }} />
      }>
        <FormAbschnitt titel="Ticket" icon={Ticket} farbe="orange" pflicht={1} index={0}>
          <FormFeld label="Titel" value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="z.B. Heizung defekt 3. OG" pflicht />
        </FormAbschnitt>

        <FormAbschnitt titel="Beschreibung" icon={TextAlignLeft} farbe="violett" index={1}>
          <FeldHuelle label="Beschreibung" alsLabel>
            <ListTextarea
              value={beschreibung}
              onChange={(e) => setBeschreibung(e.target.value)}
              rows={3}
              placeholder="Details zum Ticket..."
              glasTips
              className="w-full resize-none border-none bg-transparent text-white outline-none placeholder:text-white/30"
              style={{ font: 'inherit', fontSize: 15, fontWeight: 500, lineHeight: 1.45, padding: '2px 0' }}
            />
          </FeldHuelle>
          <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
            {checklistenVorlagen.length > 0 && (
              <FormAuswahl kompakt label="Checkliste einfügen…" anzeige="Checkliste einfügen…" anzeigeIcon={ListChecks} wert={null}
                tip="Checkliste einfügen – Ablauf aus einer Vorlage ans Ende der Beschreibung kopieren"
                optionen={checklistenVorlagen.map((v) => ({ wert: v.id, label: v.name, icon: ListChecks, iconFlaeche: ZEILEN_ICON.gruen.flaeche, iconFarbe: ZEILEN_ICON.gruen.farbe }))}
                onChange={(id) => {
                  const v = checklistenVorlagen.find((cv) => cv.id === id);
                  if (v) setBeschreibung((prev) => fuegeChecklisteAn(prev, v.checkliste));
                }} />
            )}
            <KiTextHelfer
              aktuellerText={beschreibung}
              kontext={{ titel }}
              onUebernehmen={setBeschreibung}
              richtung="unten"
              dunkel
            />
          </div>
        </FormAbschnitt>

        <FormAbschnitt titel="Einordnung" icon={Tag} farbe="orange" index={2}>
          <FormZeile>
            <FormAuswahl label="Priorität" wert={prioritaet} onChange={setPrioritaet} tip="Priorität – Wie dringend das Ticket ist"
              optionen={PRIO.map((p) => ({ ...p, icon: Circle, iconFarbe: PRIO_FARBE[p.wert], iconFlaeche: ZEILEN_ICON.dunkel.flaeche }))} />
            <FormAuswahl label="Kategorie" wert={kategorieId} onChange={(id) => { setKategorieId(id); setSubKategorieId(''); }}
              tip="Kategorie – Hauptkategorie des Tickets"
              optionen={[{ wert: '', label: '— Wählen —', ...leer }, ...hauptKategorien.map((k) => ({ wert: k.id, label: k.name, icon: Tag, ...neutral }))]} />
          </FormZeile>
          {subKategorien.length > 0 && (
            <FormAuswahl label="Unterkategorie" wert={subKategorieId} onChange={setSubKategorieId}
              optionen={[{ wert: '', label: '— Optional —', ...leer }, ...subKategorien.map((k) => ({ wert: k.id, label: k.name, icon: TagSimple, ...neutral }))]} />
          )}
        </FormAbschnitt>

        <FormAbschnitt titel="Verknüpfung" icon={LinkSimple} farbe="gold" index={3}>
          <FormSuche label="Objekt verknüpfen" platzhalter="Objekt suchen..." tip="Objekt verknüpfen – Objekt, um das es im Ticket geht"
            wert={selectedObjekt ? selectedObjekt.bezeichnung : objektSearch}
            onChange={(e) => { setObjektSearch(e.target.value); setShowObjektSearch(true); }}
            onFocus={() => setShowObjektSearch(true)} onSchliessen={() => setShowObjektSearch(false)}
            offen={showObjektSearch} mindestLaenge={2} festNachAuswahl ausgewaehlt={!!selectedObjekt}
            onEntfernen={() => setSelectedObjekt(null)}
            treffer={selectedObjekt ? [] : objektResults.map((o) => ({
              id: o.id, label: o.bezeichnung, unter: `${o.strasse} ${o.hausnummer}, ${o.plz} ${o.stadt}`, icon: Buildings,
              iconFlaeche: ZEILEN_ICON.gold.flaeche, iconFarbe: ZEILEN_ICON.gold.farbe,
            }))}
            onWaehlen={(id) => {
              const o = objektResults.find((x) => x.id === id);
              if (o) { setSelectedObjekt(o); setObjektSearch(''); setShowObjektSearch(false); }
            }} />
          <FormSuche label="Mieter (optional)" platzhalter="Mieter suchen..."
            wert={selectedMieter ? `${selectedMieter.vorname} ${selectedMieter.nachname}` : mieterSearch}
            onChange={(e) => { setMieterSearch(e.target.value); setShowMieterSearch(true); }}
            onFocus={() => setShowMieterSearch(true)} onSchliessen={() => setShowMieterSearch(false)}
            offen={showMieterSearch} mindestLaenge={2} festNachAuswahl ausgewaehlt={!!selectedMieter}
            onEntfernen={() => setSelectedMieter(null)}
            treffer={selectedMieter ? [] : mieterResults.map((m) => ({
              id: m.id, label: `${m.vorname} ${m.nachname}`, unter: m.email ?? undefined, icon: User, ...neutral,
            }))}
            onWaehlen={(id) => {
              const m = mieterResults.find((x) => x.id === id);
              if (m) { setSelectedMieter(m); setMieterSearch(''); setShowMieterSearch(false); }
            }} />
        </FormAbschnitt>

        <FormAbschnitt titel="Zuständigkeit & Fristen" icon={CalendarCheck} farbe="gruen" index={4}>
          <FormAuswahl label="Zuständig" wert={zustaendigId} onChange={setZustaendigId}
            tip="Zuständig – Mitarbeiter oder Team – einzelne Mitarbeiter werden benachrichtigt"
            optionen={[
              { wert: '', label: '— Niemand —', ...leer },
              ...teamMembers.map((m) => ({ wert: m.id, label: m.full_name || 'Unbekannt', gruppe: 'Mitarbeiter', icon: User, iconFlaeche: ZEILEN_ICON.gruen.flaeche, iconFarbe: ZEILEN_ICON.gruen.farbe })),
              ...teams.map((t) => ({ wert: t.id, label: t.name, gruppe: 'Teams', icon: PhUsers, iconFlaeche: ZEILEN_ICON.violett.flaeche, iconFarbe: ZEILEN_ICON.violett.farbe })),
            ]} />
          <FormZeile>
            <FormFeld label="Fällig am" typ="date" value={faelligAm} onChange={(e) => setFaelligAm(e.target.value)} />
            <FormFeld label="Wiedervorlage am" typ="date" value={wiedervorlageAm} onChange={(e) => setWiedervorlageAm(e.target.value)} />
          </FormZeile>
        </FormAbschnitt>
      </FormRahmen>
    );
  }

  // immo: nackter Inhalt im neuen Immobilien-Design.
  if (immo) return inhalt;

  return (
    <div data-ticket-modal className="fixed inset-0 z-[300] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[600px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#1a1d24] p-6 rounded-t-[2rem] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-black text-white uppercase tracking-tight">Neues Ticket</h2>
            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Aufgabe oder Anfrage erstellen</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>
        {inhalt}
      </div>
    </div>
  );
};

export default TicketFormular;
